import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Bold, Italic, Underline, List, ListOrdered, Link2, Image } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { api } from '../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: #0a0e17;
  border: none;
  padding: 0.7rem 1rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
`;

const Filters = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  min-width: 220px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  table-layout: fixed;

  th, td {
    text-align: left;
    padding: 0.75rem 0.85rem;
    font-size: 0.92rem;
    vertical-align: middle;
  }

  th {
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    border-bottom: 1px solid #f1f5f9;
    color: #0f172a;
  }

  th:last-child,
  td:last-child {
    text-align: right;
    width: 260px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
  white-space: nowrap;
  justify-content: flex-end;
`;

const GhostButton = styled.button`
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const DangerButton = styled.button`
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  background: ${props => props.$status === 'published' ? 'rgba(0, 212, 170, 0.15)' : '#f1f5f9'};
  color: ${props => props.$status === 'published' ? '#0f766e' : '#64748b'};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 1.25rem;
  flex-wrap: wrap;
`;

const PageInfo = styled.span`
  color: #64748b;
  font-size: 0.9rem;
`;

const PageControls = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: ${props => props.$active ? '#0f172a' : '#f8fafc'};
  color: ${props => props.$active ? '#fff' : '#475569'};
  font-weight: 600;
  cursor: pointer;
`;

const PageSizeSelect = styled.select`
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 600;
  color: #475569;
`;

const Ellipsis = styled.span`
  padding: 0.45rem 0.6rem;
  color: #94a3b8;
  font-weight: 600;
`;

const EmptyState = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  border: 1px dashed #e2e8f0;
  color: #64748b;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 50;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 18px;
  padding: 1.75rem;
  width: min(900px, 92vw);
  max-height: 90vh;
  overflow-y: auto;
  z-index: 60;
  border: 1px solid #e2e8f0;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
`;

const ModalTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  color: #0f172a;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.35rem;
  cursor: pointer;
  color: #64748b;
  width: 36px;
  height: 36px;
  border-radius: 50%;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  padding: 1.1rem;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  margin: 0 0 0.85rem;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  grid-column: span ${props => props.$span ?? 1};

  @media (max-width: 820px) {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
`;

const Input = styled.input`
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  min-height: 120px;
  resize: vertical;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const Select = styled.select`
  padding: 0.7rem 0.85rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  background: #ffffff;
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);

  &:focus {
    outline: none;
    border-color: #00b894;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.18);
  }
`;

const EditorWrapper = styled.div`
  border: 1px solid #d6dae0;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
`;

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
`;

const Editor = styled.div`
  min-height: 200px;
  padding: 12px 14px;
  outline: none;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #0f172a;
  background: white;

  img {
    max-width: 100%;
    border-radius: 12px;
    margin: 12px 0;
  }
`;

const InlineField = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const SmallInput = styled.input`
  padding: 0.55rem 0.7rem;
  border-radius: 10px;
  border: 1px solid #d6dae0;
  flex: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PreviewCover = styled.div<{ $src?: string }>`
  height: 180px;
  border-radius: 12px;
  margin-bottom: 0.75rem;
  background: ${props => props.$src
    ? `linear-gradient(180deg, rgba(10,14,23,0) 0%, rgba(10,14,23,0.35) 100%), url(${props.$src})`
    : 'linear-gradient(135deg, rgba(0, 212, 170, 0.2), rgba(8, 145, 178, 0.2))'};
  background-size: cover;
  background-position: center;
`;

const PreviewExcerpt = styled.p`
  color: #64748b;
  margin-bottom: 0.75rem;
`;

const PreviewBox = styled.div`
  border: 1px dashed #d6dae0;
  border-radius: 12px;
  padding: 1rem;
  background: #f8fafc;
`;

const PreviewTitle = styled.h3`
  font-size: 0.95rem;
  color: #334155;
  margin-bottom: 0.6rem;
`;

const PreviewBody = styled.div`
  font-size: 0.95rem;
  color: #0f172a;
  line-height: 1.6;
`;

type BlogForm = {
  id?: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tags: string;
  cover_image: string;
  status: 'draft' | 'published';
  published_at: string;
};

const emptyForm: BlogForm = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  tags: '',
  cover_image: '',
  status: 'draft',
  published_at: '',
};

export const AdminBlogPage: React.FC = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [form, setForm] = useState<BlogForm>(emptyForm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const loadPosts = async () => {
    const response = await api.get('/admin/blog/posts');
    setPosts(response.data.data || []);
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const filteredPosts = useMemo(() => {
    const base = posts.filter(post => {
      const title = (post.title || '').toLowerCase();
      const tags = Array.isArray(post.tags) ? post.tags.join(' ').toLowerCase() : '';
      const matchesSearch = title.includes(search.toLowerCase()) || tags.includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    const sorted = [...base];
    sorted.sort((a, b) => {
      if (sortBy === 'title_desc') {
        return (b.title || '').localeCompare(a.title || '');
      }
      if (sortBy === 'title_asc') {
        return (a.title || '').localeCompare(b.title || '');
      }
      const dateA = new Date(a.published_at || a.created_at || 0).getTime();
      const dateB = new Date(b.published_at || b.created_at || 0).getTime();
      if (sortBy === 'date_asc') {
        return dateA - dateB;
      }
      return dateB - dateA;
    });
    return sorted;
  }, [posts, search, statusFilter, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sortBy, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedPosts = filteredPosts.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages: number[] = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push(-1);
    }

    for (let pageIndex = start; pageIndex <= end; pageIndex += 1) {
      pages.push(pageIndex);
    }

    if (end < totalPages - 1) {
      pages.push(-1);
    }

    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!isModalOpen || !editorRef.current) return;
    editorRef.current.innerHTML = form.content || '';
  }, [isModalOpen, form.content, form.id]);

  const openNew = () => {
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEdit = (post: any) => {
    setForm({
      id: post.id,
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      cover_image: post.cover_image || '',
      status: post.status || 'draft',
      published_at: post.published_at ? post.published_at.slice(0, 16) : '',
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      excerpt: form.excerpt || undefined,
      content: form.content,
      tags: form.tags ? form.tags.split(',').map(tag => tag.trim()).filter(Boolean) : undefined,
      cover_image: form.cover_image || undefined,
      status: form.status,
      published_at: form.published_at || undefined,
    };

    if (form.id) {
      await api.put(`/admin/blog/posts/${form.id}`, payload);
    } else {
      await api.post('/admin/blog/posts', payload);
    }

    closeModal();
    loadPosts();
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm('Deseja remover este post?')) return;
    await api.delete(`/admin/blog/posts/${postId}`);
    loadPosts();
  };

  const toggleStatus = async (post: any) => {
    await api.put(`/admin/blog/posts/${post.id}`, {
      status: post.status === 'published' ? 'draft' : 'published',
      published_at: post.status === 'published' ? null : new Date().toISOString(),
    });
    loadPosts();
  };

  const exec = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setForm(prev => ({ ...prev, content: editorRef.current?.innerHTML || '' }));
    }
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    setForm(prev => ({ ...prev, content: editorRef.current?.innerHTML || '' }));
  };

  const uploadImage = async (file: File) => {
    const data = new FormData();
    data.append('image', file);
    const response = await api.post('/admin/blog/upload', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data.url as string;
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    exec('insertImage', url);
  };

  const handleCoverUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    setForm(prev => ({ ...prev, cover_image: url }));
  };

  const handleInsertImageUrl = () => {
    if (!imageUrl.trim()) return;
    exec('insertImage', imageUrl.trim());
    setImageUrl('');
  };

  return (
    <AdminLayout>
      <PageHeader>
        <Title>Blog</Title>
        <Filters>
          <SearchInput
            placeholder="Buscar por titulo ou tag..."
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
          <Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
            <option value="all">Todos</option>
            <option value="published">Publicados</option>
            <option value="draft">Rascunhos</option>
          </Select>
          <Select value={sortBy} onChange={event => setSortBy(event.target.value)}>
            <option value="date_desc">Data mais recente</option>
            <option value="date_asc">Data mais antiga</option>
            <option value="title_asc">Titulo A-Z</option>
            <option value="title_desc">Titulo Z-A</option>
          </Select>
          <PrimaryButton type="button" onClick={openNew}>Novo Post</PrimaryButton>
        </Filters>
      </PageHeader>

      {filteredPosts.length === 0 ? (
        <EmptyState>
          {posts.length === 0 ? 'Nenhum post cadastrado ainda.' : 'Nenhum post encontrado para os filtros atuais.'}
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Titulo</th>
              <th>Status</th>
              <th>Publicacao</th>
              <th>Tags</th>
              <th>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {pagedPosts.map(post => (
              <tr key={post.id}>
                <td>{post.title}</td>
                <td><StatusBadge $status={post.status}>{post.status === 'published' ? 'Publicado' : 'Rascunho'}</StatusBadge></td>
                <td>{post.published_at ? new Date(post.published_at).toLocaleDateString('pt-BR') : '—'}</td>
                <td>{Array.isArray(post.tags) ? post.tags.join(', ') : '—'}</td>
                <td>
                  <Actions>
                    <GhostButton type="button" onClick={() => openEdit(post)}>Editar</GhostButton>
                    <GhostButton type="button" onClick={() => toggleStatus(post)}>
                      {post.status === 'published' ? 'Despublicar' : 'Publicar'}
                    </GhostButton>
                    <DangerButton type="button" onClick={() => handleDelete(post.id)}>Excluir</DangerButton>
                  </Actions>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {filteredPosts.length > 0 && (
        <Pagination>
          <PageInfo>Pagina {currentPage} de {totalPages}</PageInfo>
          <PageControls>
            <PageButton
              type="button"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </PageButton>
            {pageNumbers.map((pageNumber, index) => (
              pageNumber === -1 ? (
                <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
              ) : (
                <PageButton
                  key={pageNumber}
                  type="button"
                  $active={pageNumber === currentPage}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </PageButton>
              )
            ))}
            <PageButton
              type="button"
              onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Proxima
            </PageButton>
            <PageSizeSelect value={pageSize} onChange={event => setPageSize(Number(event.target.value))}>
              <option value={10}>10 / pagina</option>
              <option value={20}>20 / pagina</option>
              <option value={50}>50 / pagina</option>
            </PageSizeSelect>
          </PageControls>
        </Pagination>
      )}

      {isModalOpen && (
        <>
          <ModalOverlay onClick={closeModal} />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{form.id ? 'Editar Post' : 'Novo Post'}</ModalTitle>
              <CloseButton type="button" onClick={closeModal} aria-label="Fechar">×</CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <Section>
                <SectionTitle>Informacoes basicas</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Titulo</Label>
                    <Input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} required />
                  </Field>
                  <Field>
                    <Label>Slug (opcional)</Label>
                    <Input value={form.slug} onChange={event => setForm({ ...form, slug: event.target.value })} />
                  </Field>
                  <Field $span={2}>
                    <Label>Resumo</Label>
                    <TextArea value={form.excerpt} onChange={event => setForm({ ...form, excerpt: event.target.value })} />
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Conteudo</SectionTitle>
                <FieldGrid>
                  <Field $span={2}>
                    <Label>Conteudo</Label>
                    <EditorWrapper>
                      <Toolbar>
                        <ToolButton type="button" onClick={() => exec('bold')}><Bold size={16} /></ToolButton>
                        <ToolButton type="button" onClick={() => exec('italic')}><Italic size={16} /></ToolButton>
                        <ToolButton type="button" onClick={() => exec('underline')}><Underline size={16} /></ToolButton>
                        <ToolButton type="button" onClick={() => exec('insertUnorderedList')}><List size={16} /></ToolButton>
                        <ToolButton type="button" onClick={() => exec('insertOrderedList')}><ListOrdered size={16} /></ToolButton>
                        <ToolButton type="button" onClick={() => {
                          const url = window.prompt('URL do link');
                          if (url) exec('createLink', url);
                        }}>
                          <Link2 size={16} />
                        </ToolButton>
                        <ToolButton type="button" onClick={() => fileInputRef.current?.click()}>
                          <Image size={16} />
                        </ToolButton>
                      </Toolbar>
                      <Editor
                        ref={editorRef}
                        contentEditable
                        onInput={handleEditorInput}
                        suppressContentEditableWarning
                      />
                    </EditorWrapper>
                    <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
                    <InlineField>
                      <SmallInput
                        placeholder="Inserir imagem via URL"
                        value={imageUrl}
                        onChange={event => setImageUrl(event.target.value)}
                      />
                      <GhostButton type="button" onClick={handleInsertImageUrl}>Inserir</GhostButton>
                    </InlineField>
                  </Field>
                </FieldGrid>
              </Section>
              <Section>
                <SectionTitle>Preview</SectionTitle>
                <PreviewBox>
                  <PreviewTitle>Preview</PreviewTitle>
                  <PreviewBody>
                    <PreviewCover $src={form.cover_image || undefined} />
                    {form.excerpt && <PreviewExcerpt>{form.excerpt}</PreviewExcerpt>}
                    {form.content ? (
                      <div dangerouslySetInnerHTML={{ __html: form.content }} />
                    ) : (
                      <div>Sem conteudo para visualizar.</div>
                    )}
                  </PreviewBody>
                </PreviewBox>
              </Section>
              <Section>
                <SectionTitle>Publicacao</SectionTitle>
                <FieldGrid>
                  <Field>
                    <Label>Tags (separadas por virgula)</Label>
                    <Input value={form.tags} onChange={event => setForm({ ...form, tags: event.target.value })} />
                  </Field>
                  <Field>
                    <Label>Capa (URL)</Label>
                    <Input value={form.cover_image} onChange={event => setForm({ ...form, cover_image: event.target.value })} />
                    <input type="file" accept="image/*" onChange={handleCoverUpload} />
                  </Field>
                  <Field>
                    <Label>Status</Label>
                    <Select value={form.status} onChange={event => setForm({ ...form, status: event.target.value as BlogForm['status'] })}>
                      <option value="draft">Rascunho</option>
                      <option value="published">Publicado</option>
                    </Select>
                  </Field>
                  <Field>
                    <Label>Publicar em</Label>
                    <Input
                      type="datetime-local"
                      value={form.published_at}
                      onChange={event => setForm({ ...form, published_at: event.target.value })}
                    />
                  </Field>
                </FieldGrid>
              </Section>
              <ButtonRow>
                <GhostButton type="button" onClick={closeModal}>Cancelar</GhostButton>
                <PrimaryButton type="submit">Salvar</PrimaryButton>
              </ButtonRow>
            </Form>
          </ModalContent>
        </>
      )}
    </AdminLayout>
  );
};
